import type { FC } from 'react'

import { AssetFallbackIcon } from '@skytech/ui'
import { ImageWithIconFallback } from '@skytech/ui/Image'
import { Td } from '@skytech/ui/Table/Td'
import PailOutlineIcon from 'mdi-react/PailOutlineIcon'
import RecycleIcon from 'mdi-react/RecycleIcon'
import { twJoin } from 'tailwind-merge'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { FormattedNumberWithCurrency } from '@/components/FormattedNumberWithCurrency'
import Highlight from '@/components/Highlight'
import { LocalDate } from '@/components/LocalDate'
import { RouterLink } from '@/components/RouterLink'
import { TrLink } from '@/components/TrLink'
import { useIsAdmin, useIsThreeStepIT } from '@/hooks/access'
import type { Asset } from '@/modules/admin/resources'
import { APP_ROUTES } from '@/util/app-routes'

import type { AssetUserType } from '../../resources'

import './AssetListItem.styles.scss'

export const AssetListItem: FC<
  Asset & {
    searchFilter: string
  }
> = ({
  costObjectName,
  imageUrl,
  assetDescription,
  id,
  searchFilter,
  costObjectId,
  received,
  cost,
  address,
  addressCity,
  addressZip,
  status,
  userType,
  imeiSnr,
  assetType,
  assetCurrency,
}) => {
  const translate = useTranslation()

  return (
    <TrLink path={APP_ROUTES.HARDWARE_ASSET.replace(':id', String(id))}>
      <Td className="hidden md:table-cell">
        <div className="asset-name">
          <ImageWithIconFallback
            src={imageUrl || ''}
            alt="asset"
            className="hidden md:table-cell"
            fallbackIcon={<AssetFallbackIcon assetType={assetType} />}
          />

          <AssetCategoryIcon userType={userType} />
        </div>
      </Td>

      <Td>
        <div>
          <Highlight pattern={searchFilter} text={assetDescription} />
        </div>
        <div className="subtitle is-mobile">
          <Highlight pattern={searchFilter} text={costObjectName} />
        </div>
        <div className="flex gap-1">
          <div className="subtitle hidden md:table-cell">
            <Highlight pattern={searchFilter} text={translate(`asset_type/${assetType}`)} />
          </div>
          <div className="subtitle hidden md:table-cell">
            <Highlight pattern={searchFilter} text={imeiSnr} />
          </div>
        </div>
      </Td>

      <Td className="hidden md:table-cell">
        <div>
          {costObjectName === 'Expired' ? (
            <Translation id="User unavailable" />
          ) : (
            <AssetUser
              searchFilter={searchFilter}
              costObjectName={costObjectName}
              costObjectId={costObjectId}
            />
          )}
        </div>
        <Highlight pattern={searchFilter} text={translate(`asset_user_type/${userType}`)} />
      </Td>

      <Td className="hidden md:table-cell">
        <Highlight pattern={searchFilter} text={translate(`asset_status/${status}`)} />
      </Td>

      <Td className="hidden md:table-cell">
        <Highlight pattern={searchFilter} text={address} />
        <div className="subtitle">
          <Highlight pattern={searchFilter} text={addressZip} />{' '}
          <Highlight pattern={searchFilter} text={addressCity} />
        </div>
      </Td>

      <Td className="hidden md:table-cell">
        <LocalDate date={received} />
      </Td>

      <Td className="hidden md:table-cell">
        {cost !== null && cost > 0 ? (
          <FormattedNumberWithCurrency amount={cost} currency={assetCurrency} />
        ) : (
          <span> — </span>
        )}
      </Td>
    </TrLink>
  )
}

const AssetCategoryIcon: FC<{ userType: AssetUserType }> = ({ userType }) => {
  if (!['GREENPOOL', 'POOL'].includes(userType)) return null
  const isThreeStepIT = useIsThreeStepIT()

  return (
    <div className="duration-250 absolute -right-1 bottom-0 flex h-7 w-7 items-center justify-center overflow-hidden rounded-full bg-white p-1 transition ease-in">
      {userType === 'GREENPOOL' && (
        <RecycleIcon className={twJoin(isThreeStepIT ? 'fill-threeStepIT' : 'fill-green-500')} />
      )}
      {userType === 'POOL' && (
        <PailOutlineIcon
          className={twJoin(isThreeStepIT ? 'fill-threeStepIT' : 'fill-green-500')}
        />
      )}
    </div>
  )
}

type AssetUserProps = {
  searchFilter: string
} & Pick<Asset, 'costObjectName' | 'costObjectId'>

const AssetUser: FC<AssetUserProps> = ({ searchFilter, costObjectId, costObjectName }) => {
  const isAdmin = useIsAdmin()

  if (isAdmin) {
    return (
      <RouterLink
        to={APP_ROUTES.EMPLOYEE.replace(':costObjectId', String(costObjectId))}
        onClick={e => {
          e.stopPropagation()
        }}
      >
        <Highlight pattern={searchFilter} text={costObjectName} />
      </RouterLink>
    )
  }
  return <Highlight pattern={searchFilter} text={costObjectName} />
}
